import {
  AssignmentPolicy,
  CaseEmailTemplate,
  CaseState,
  CaseTemplateStatus,
} from 'dashboard/models/case-template';
import { EnvironmentType } from 'dashboard/models/environment';
import { Graph } from 'dashboard/models/graph';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { List, ListAttributes } from 'dashboard/models/list';
import { ReportTemplate } from 'dashboard/models/report-template';
import { WorkflowSchema as _WorkflowSchema } from 'dashboard/models/workflow-schema';
import { FieldSchema } from 'lib/fields';
import { ApiListRelationship, ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { RelationSchema } from 'lib/relations';

export enum WorkflowStatus {
  Paused = 'paused',
  Running = 'running',
  Archived = 'archived',
}

export interface WorkflowAttributes {
  status: WorkflowStatus;
  active: boolean;
  name: string;
  description: string;
  version: number;
  'created-at': string;
  'updated-at': string;
  'environment-id': string;
  'trigger-type': string;
}

export interface WorkflowRelationships {
  'active-deployment': ApiRelationship;
  'latest-draft-version': ApiRelationship;
  'latest-published-version': ApiRelationship;
  environments: ApiListRelationship<EnvironmentType>;
  'case-templates': ApiListRelationship<ApiModel.CaseTemplate>;
}

export type Workflow = ApiResource<WorkflowAttributes, WorkflowRelationships, ApiModel.Workflow>;

export enum WorkflowOperatorKey {
  Equals = '=',
  DoubleEquals = '==',
  DoesNotEqual = '!=',
  Concat = 'cat',
  Contains = 'contains',
  ContainsAnySubstring = 'containsanysubstring',
  ContainsNoSubstring = 'containsnosubstring',
  StartsWith = 'startswith',
  LessThan = '<',
  LessThanOrEqual = '<=',
  GreaterThan = '>',
  GreaterThanOrEqual = '>=',
  VersionLessThan = 'versionlessthan',
  VersionLessThanOrEqual = 'versionlessthanorequal',
  VersionGreaterThan = 'versiongreaterthan',
  VersionGreaterThanOrEqual = 'versiongreaterthanorequal',
  In = 'in',
  InList = 'inlist',
  NotIn = 'nin',
  NotInList = 'ninlist',
  IncludesAll = 'includesall',
  IncludesNotAll = 'includesnotall',
  IncludesOnly = 'includesonly',
  IncludesNone = 'includesnone',
  IncludesExactly = 'includesexactly',
  IncludesNotExactly = 'includesnotexactly',
  IsBlank = 'isblank',
  IsNotBlank = 'isnotblank',
  IsFalsy = '!',
  IsTruthy = '!!',
  Add = '+',
  Subtract = 'subtract',
  Multiply = '*',
  Divide = '/',
  OrAssign = '||=',
  And = 'and',
  Or = 'or',
  // array operators
  Reduce = 'reduce',
  SizeGreaterThan = 'sizegreaterthan',
  SizeGreaterThanOrEqual = 'sizegreaterthanorequal',
  SizeLessThan = 'sizelessthan',
  SizeLessThanOrEqual = 'sizelessthanorequal',
  SizeEquals = 'sizeequals',

  // these operators don't actually live on the BE, they're
  // implemented using a combination of base operators and we
  // expose them as higher order operations on the FE
  Sum = 'SUM',
  Avg = 'AVG',

  AccountNameSubstringMatchAll = 'accountnamesubstringmatchall',
  AccountNameSubstringMatchNotAll = 'accountnamesubstringmatchnotall',
  AccountNameSubstringMatchAny = 'accountnamesubstringmatchany',
  AccountNameSubstringMatchNone = 'accountnamesubstringmatchnone',

  MatchesPercentage = 'matchespercentage',

  MatchesRegex = 'matchesregex',

  MatchesSubstring = 'matchessubstring',
  MismatchesSubstring = 'mismatchessubstring',

  // age related
  AgeGreaterThan = 'agegreaterthan',
  AgeGreaterThanOrEqual = 'agegreaterthanorequal',
  AgeLessThan = 'agelessthan',
  AgeLessThanOrEqual = 'agelessthanorequal',
}

export const AGE_OPERATORS = [
  WorkflowOperatorKey.AgeGreaterThan,
  WorkflowOperatorKey.AgeGreaterThanOrEqual,
  WorkflowOperatorKey.AgeLessThan,
  WorkflowOperatorKey.AgeLessThanOrEqual,
];

export const ARRAY_AND_MULTI_ENUM_SIZE_OPERATORS = [
  WorkflowOperatorKey.SizeGreaterThan,
  WorkflowOperatorKey.SizeGreaterThanOrEqual,
  WorkflowOperatorKey.SizeLessThan,
  WorkflowOperatorKey.SizeLessThanOrEqual,
  WorkflowOperatorKey.SizeEquals,
];

export const VERSION_OPERATORS = [
  WorkflowOperatorKey.VersionGreaterThan,
  WorkflowOperatorKey.VersionGreaterThanOrEqual,
  WorkflowOperatorKey.VersionLessThan,
  WorkflowOperatorKey.VersionLessThanOrEqual,
];

export interface WorkflowFieldBase {
  operators: WorkflowOperatorKey[];
  type: WorkflowFieldType;
}

export interface WorkflowFieldString extends WorkflowFieldBase {
  type: WorkflowFieldType.String;
  possibilities?: null;
}

export interface WorkflowFieldBoolean extends WorkflowFieldBase {
  type: WorkflowFieldType.Boolean;
  possibilities?: [true, false];
}

export interface WorkflowFieldEnum extends WorkflowFieldBase {
  type: WorkflowFieldType.Enum;
  possibilities?: string[];
}

export interface WorkflowFieldCategorizedEnum extends WorkflowFieldBase {
  type: WorkflowFieldType.CategorizedEnum;
  possibilities?: Record<string, string[]>;
}

export interface WorkflowFieldCustom extends WorkflowFieldBase {
  type: WorkflowFieldType.Custom;
  possibilities?: string[];
}

export interface WorkflowFieldRange extends WorkflowFieldBase {
  type: WorkflowFieldType.Range;
  possibilities: [MinPossibility: string, MaxPossibility: string];
}

export interface WorkflowFieldDate extends WorkflowFieldBase {
  type: WorkflowFieldType.Date;
}

export interface WorkflowFieldTime extends WorkflowFieldBase {
  type: WorkflowFieldType.Time;
  possibilities?: null;
}

export interface WorkflowFieldField extends WorkflowFieldBase {
  type: WorkflowFieldType.Field;
  operators: [];
  possibilities: WorkflowPossibilityField[];
}

export interface WorkflowPossibilityField {
  name: string;
  type: 'string' | 'boolean' | 'number' | 'date';
  operators: WorkflowOperatorKey[];
}

export enum WorkflowFieldType {
  Enum = 'enum',
  CategorizedEnum = 'categorized-enum',
  Custom = 'custom',
  Range = 'range',
  Time = 'time',
  Field = 'field',
  Boolean = 'boolean',
  String = 'string',
  Date = 'date',
}

// TODO @jkim - make this use ApiModel
export enum WorkflowFieldObjectKey {
  Account = 'account',
  AccountType = 'account-type',
  Case = 'case',
  CaseMessage = 'case-message',
  CaseTemplate = 'case-template',
  DocumentGeneric = 'document/generic',
  DocumentGovernmentId = 'document/government-id',
  Environment = 'environment',
  Error = 'error',
  Generic = 'generic',
  GraphQuery = 'graph-query',
  Inquiry = 'inquiry',
  SAR = 'sar',
  InquiryTemplateVersion = 'inquiry-template-version',
  ReportAddressLookup = 'report/address-lookup',
  ReportAdverseMedia = 'report/adverse-media',
  ReportBusinessAdverseMedia = 'report/business-adverse-media',
  ReportBusinessAssociatedPersons = 'report/business-associated-persons',
  ReportBusinessClassification = 'report/business-classification',
  ReportBusinessIndustryClassification = 'report/business-industry-classification',
  ReportBusinessLiensLookup = 'report/business-liens-lookup',
  ReportBusinessLookup = 'report/business-lookup',
  ReportBusinessNonprofit = 'report/business-nonprofit',
  ReportBusinessOnlinePresence = 'report/business-online-presence',
  ReportBusinessRegistrationsLookup = 'report/business-registrations-lookup',
  ReportBusinessSocialMedia = 'report/business-social-media',
  ReportBusinessWatchlist = 'report/business-watchlist',
  ReportBusinessWebsite = 'report/business-website',
  ReportChainalysisAddressScreening = 'report/integration/chainalysis-address-screening',
  ReportClearbitBusinessLookup = 'report/integration/clearbit-business-lookup',
  ReportCoinbaseCheckCryptoRisk = 'report/coinbase-check-crypto-risk',
  ReportCryptoAddressWatchlist = 'report/crypto-address-watchlist',
  ReportCustomList = 'report/custom-list',
  ReportEmailAddress = 'report/email-address',
  ReportEquifaxOneview = 'report/integration/equifax-oneview',
  ReportKyckrBusinessLookup = 'report/integration/kyckr-business-lookup',
  ReportMiddeskBusinessLookup = 'report/integration/middesk-business-lookup',
  ReportMxAccount = 'report/integration/mx-account',
  ReportPhoneNumber = 'report/phone-number',
  ReportPoliticallyExposedPerson = 'report/politically-exposed-person',
  ReportProfile = 'report/profile',
  ReportProfileNonAuthoritative = 'report/profile-non-authoritative',
  ReportScreening = 'report/screening',
  ReportSentilinkApplicationRisk = 'report/integration/sentilink-application-risk',
  ReportSentilinkScores = 'report/integration/sentilink-scores',
  ReportSocialMedia = 'report/social-media',
  ReportSynthetic = 'report/synthetic',
  ReportTrmWalletScreening = 'report/integration/trm-wallet-screening',
  ReportWatchlist = 'report/watchlist',
  ReportTemplate = 'report-template',
  SelfieFileImage = 'selfie-file/image',
  Template = 'template',
  Transaction = 'transaction',
  TransactionType = 'transaction-type',
  VerificationAamva = 'verification/aamva',
  VerificationBusinessWebsite = 'verification/business-website',
  VerificationCertificateKorea = 'verification/certificate-korea',
  VerificationDatabase = 'verification/database',
  VerificationDatabaseAadhaar = 'verification/database-aadhaar',
  VerificationDatabaseBusiness = 'verification/database-business',
  VerificationDatabaseBusinessFootprint = 'verification/database-business-footprint',
  VerificationDatabaseEcbsv = 'verification/database-ecbsv',
  VerificationDatabasePhoneCarrier = 'verification/database-phone-carrier',
  VerificationDatabaseStandard = 'verification/database-standard',
  VerificationDatabaseSerpro = 'verification/database-serpro',
  VerificationDatabaseTin = 'verification/database-tin',
  VerificationDatabaseVatNumber = 'verification/database-vat-number',
  VerificationDigitalIdFinnishTrustNetwork = 'verification/digital-id-finnish-trust-network',
  VerificationDigitalIdOneId = 'verification/digital-id-one-id',
  VerificationDigitalIdSingpass = 'verification/digital-id-singpass',
  VerificationDigitalIdUkSharecode = 'verification/digital-id-uk-sharecode',
  VerificationDocument = 'verification/document',
  VerificationEmailAddress = 'verification/email-address',
  VerificationGovernmentId = 'verification/government-id',
  VerificationGovernmentIdNfc = 'verification/government-id-nfc',
  VerificationPhoneNumber = 'verification/phone-number',
  VerificationMobileDriversLicense = 'verification/mobile-drivers-license',
  VerificationSelfie = 'verification/selfie',
  User = 'user',
}

export enum WorkflowFieldVariableKey {
  Analytics = 'analytics',
  Calculate = 'calculate',
  CalculateVariable = 'calculate-variable',
  Code = 'code',
  GptGeneratedMessage = 'gpt-generated-message',
  HttpRequest = 'http-request',
  HubspotObjectId = 'hubspot-object-id',
  InputsHash = 'inputs-hash',
  InputVariable = 'input-variable',
  InquiryFlowStateProperty = 'inquiry-flow-state-property',
  IntegrationObjectId = 'integration-object-id',
  Loop = 'loop',
  LoopCurrentItem = 'loop-current-item',
  MapOutput = 'map-output',
  Matched = 'matched',
  Module = 'module',
  MxUser = 'mx-user',
  MxMicrodepositStatus = 'mx-microdeposit-status',
  MxWidgetUrl = 'mx-widget-url',
  PlatformIntegration = 'platform-integration',
  Query = 'query',
  SalesforceRecord = 'salesforce-record',
  StandardizeIdDetails = 'standardize-id-details',
  TriggerAccount = 'trigger-account',
  TriggerApi = 'trigger-api',
  TriggerCase = 'trigger-case',
  ZendeskTicket = 'zendesk-ticket',
}

export type WorkflowObjectKey = WorkflowFieldObjectKey | WorkflowFieldVariableKey;

// maps to all the fields available
// Each of these fields corresponds to a criteria variable under app/lib/criteria_variable_schema/
export enum WorkflowFieldKey {
  AbuseScore = 'abuse-score',
  AccountStatus = 'account-status',
  AddressCategoryRiskScoreLevel = 'address-category-risk-score-level',
  AddressCity = 'address-city',
  AddressCityMatchLevel = 'address-city-match-level',
  AddressCountryCode = 'address-country-code',
  AddressMatch = 'address-match',
  AddressPostalCode = 'address-postal-code',
  AddressPostalCodeMatchLevel = 'address-postal-code-match-level',
  AddressRiskScore = 'address-risk-score',
  AddressStreet1 = 'address-street1',
  AddressStreet1MatchLevel = 'address-street1-match-level',
  AddressStreet2 = 'address-street2',
  AddressSubdivision = 'address-subdivision',
  AddressSubdivisionMatchLevel = 'address-subdivision-match-level',
  AdverseMediaTypes = 'adverse-media-types',
  Age = 'age',
  AgeThresholdComparisonRiskLevel = 'age-threshold-comparison-risk-level',
  AgeThresholdComparisonRiskScore = 'age-threshold-comparison-risk-score',
  AnyPriorInquiryStatus = 'any-prior-inquiry-status',
  BehaviorThreatLevel = 'behavior-threat-level',
  Birthdate = 'birthdate',
  Birthplace = 'birthplace',
  BrowserCount = 'browser-count',
  BrowserFingerprints = 'browser-fingerprints',
  BrowserResolvedUrl = 'browser-resolved-url',
  BrowserTypes = 'browser-types',
  BusinessEntityType = 'business-entity-type',
  BusinessFormationDate = 'business-formation-date',
  BusinessFormationSubdivision = 'business-formation-subdivision',
  BusinessIndustry = 'business-industry',
  BusinessName = 'business-name',
  BusinessNameMatchLevel = 'business-name-match-level',
  CaliforniaAttorneyGeneralStatus = 'california-attorney-general-status',
  CaptureMethods = 'capture-methods',
  CarrierRouteType = 'carrier-route-type',
  CaseResolution = 'resolution',
  CaseState = 'state',
  CheckReasons = 'check-reasons',
  Checks = 'checks',
  ClientMobileSdkVersion = 'client-mobile-sdk-version',
  ClientOsName = 'client-os-name',
  CommercialMailReceivingAgency = 'commercial-mail-receiving-agency',
  CompanyAnnualRevenue = 'company-annual-revenue',
  CompanyCountryCode = 'company-country-code',
  CompanyDescription = 'company-description',
  CompanyDomain = 'company-domain',
  CompanyEin = 'company-ein',
  CompanyEmployeesRange = 'company-employees-range',
  CompanyEstimatedAnnualRevenue = 'company-estimated-annual-revenue',
  CompanyIndustry = 'company-industry',
  CompanyLinkedin = 'company-linkedin',
  CompanyMarketCap = 'company-market-cap',
  CompanyRaised = 'company-raised',
  CompanySubindustry = 'company-subindustry',
  CompanyTag = 'company-tags',
  CorporateRegistrationStatus = 'corporate-registration-statuses',
  Country = 'country',
  CountryCode = 'country-code',
  CountryComparison = 'country-comparison',
  CountryFromSession = 'country-from-session',
  CreatedAt = 'created-at',
  Creator = 'creator',
  Data = 'data',
  DatabaseTinVerificationTinType = 'database-tin-verification-tin-type',
  DatabaseVerificationAddressMatch = 'database-verification-address-match',
  DatabaseVerificationIdentityMatchFailed = 'database-verification-identity-match-failed',
  DatabaseVerificationSsnEditDistance = 'database-verification-ssn-edit-distance',
  DeceasedDetection = 'deceased-detection',
  Designations = 'designations',
  Deliverable = 'deliverable',
  DeviceCount = 'device-count',
  DeviceFingerprints = 'device-fingerprints',
  DeviceNames = 'device-names',
  DisallowedTypeDetection = 'disallowed-type-detection',
  DisplayPhoto = 'display-photo',
  DistractionEvents = 'distraction-events',
  DocumentCheckScore = 'document-check-score',
  DocumentExtractions = 'document-extractions',
  DocumentNumber = 'document-number',
  DocumentSimilarityScore = 'document-similarity-score',
  DocumentTypes = 'document-types',
  EmailAddress = 'email-address',
  EmailAddressDomain = 'email-address-domain',
  EmailAddressDomainCreatedDays = 'email-address-domain-created-days',
  EmailAddressDomainExists = 'email-address-domain-exists',
  EmailAddressDomainReputations = 'email-address-domain-reputations',
  EmailAddressEstimatedAgeDays = 'email-address-estimated-age-days',
  EmailAddressFirstSeenDays = 'email-address-first-seen-days',
  EmailAddressIsBlacklisted = 'email-address-is-blacklisted',
  EmailAddressIsDeliverable = 'email-address-is-deliverable',
  EmailAddressIsDisposable = 'email-address-is-disposable',
  EmailAddressIsFreeProvider = 'email-address-is-free-provider',
  EmailAddressIsSpam = 'email-address-is-spam',
  EmailAddressIsSuspicious = 'email-address-is-suspicious',
  EmailAddressIsValidMx = 'email-address-is-valid-mx',
  EmailAddressLastSeenDays = 'email-address-last-seen-days',
  EmailAddressReferenceCount = 'email-address-reference-count',
  EmailAddressReputations = 'email-address-reputations',
  EmailAddressSMTPScore = 'smtp-score',
  EmailAddressTimedOut = 'timed-out',
  EmailAddressSuspect = 'suspect',
  EmailAddressCatchAll = 'catch-all',
  ExpirationDate = 'expiration-date',
  FacebookUrl = 'facebook-url',
  FailedAamvaVerificationCount = 'failed-aamva-verification-count',
  FailedChecks = 'failed-checks',
  CheckFailureReasons = 'check-failure-reasons',
  FailedChecksFromLatestVerifications = 'failed-checks-from-latest-verifications',
  FailedDatabaseBusinessVerificationCount = 'failed-database-business-verification-count',
  FailedDatabaseEcbsvVerificationCount = 'failed-database-ecbsv-verification-count',
  FailedDatabaseVerificationCount = 'failed-database-verification-count',
  FailedDocumentVerificationCount = 'failed-document-verification-count',
  FailedElectronicReplicaReasons = 'failed-electronic-replica-reasons',
  FailedGovernmentIdVerificationCount = 'failed-government-id-verification-count',
  FailedMatchGroup = 'failed-match-groups',
  FailedSelfieVerificationCount = 'failed-selfie-verification-count',
  FailedVerificationAttempts = 'failed-verification-attempts',
  FicoScore8EquifaxDataNonFacta = 'fico-score8-equifax-data-non-facta',
  FicoScore9EquifaxDataNonFacta = 'fico-score9-equifax-data-non-facta',
  Fields = 'fields',
  FilledProperties = 'filled-properties',
  FirstIntegrationType = 'first-integration-type',
  FirstPartySyntheticScore = 'first-party-synthetic-score',
  FlowState = 'flow-state',
  FromEmailAddress = 'from-email-address',
  GeolocationToResidencyDelta = 'geolocation-to-residency-delta',
  Geolocations = 'geolocations',
  GovernmentIdDesignations = 'government-id-designations',
  GovernmentIdExpirationDate = 'government-id-expiration-date',
  GovernmentIdExpirationDelta = 'government-id-expiration-delta',
  GovernmentIdExpired = 'government-id-expired',
  GovernmentIdInquiryComparisonMatchFailed = 'government-id-inquiry-comparison-match-failed',
  GovernmentIdTags = 'government-id-tags',
  GovernmentIdUnprocessableSubmissionDetectionReason = 'government-id-unprocessable-submission-detection-reasons',
  GovernmentIdVehicleClasses = 'government-id-vehicle-classes',
  GovernmentIdVisaStatus = 'government-id-visa-status',
  GraphQueryAccountStatuses = 'account-statuses',
  GraphQueryAccountTags = 'account-tags',
  GraphQueryAccountTokens = 'account-tokens',
  GraphQueryInquiryStatuses = 'inquiry-statuses',
  GraphQueryNumberOfAccountNodes = 'number-of-account-nodes',
  HasDocument = 'has-document',
  HasGovernmentIdExtraction = 'has-government-id-extraction',
  HasSelfie = 'has-selfie',
  HasStarted = 'has-started',
  HasUnknownId = 'has-unknown-id',
  HighestSimilarityLevel = 'highest-similarity-level',
  Homepage = 'homepage',
  Id = 'id',
  IdClass = 'id-class',
  IdClasses = 'id-classes',
  IdTheftScore = 'id-theft-score',
  IdentificationClass = 'identification-class',
  IdentificationNumber = 'identification-number',
  IdentificationNumberMap = 'identification-number-map',
  IncognitoBrowsingDetected = 'incognito-browsing-detected',
  InvalidFormatDetection = 'invalid-format-detection',
  IpAddresses = 'ip-addresses',
  IpCount = 'ip-count',
  IpIspList = 'ip-isp-list',
  IpServiceType = 'ip-service-type',
  IrsPublication78Verified = 'irs-publication78-verified',
  IsContinuousMatch = 'is-continuous-match',
  IsHighRisk = 'is-high-risk',
  IsOnCaliforniaFranchiseTaxBoardRevokedList = 'is-on-california-franchise-tax-board-revoked-list',
  IsOnIrsBusinessMasterFile = 'is-on-irs-business-master-file',
  IsReligiousOrganization = 'is-religious-organization',
  IssueDate = 'issue-date',
  IssuingCountry = 'issuing-country',
  IssuingSubdivision = 'issuing-subdivision',
  Keywords = 'keywords',
  Label = 'label',
  LastTwoVerificationsGeolocationDelta = 'last-two-verifications-geolocation-delta',
  LatestSessionDevice = 'latest-session-device',
  LegalEntityType = 'legal-entity-type',
  LinkUrl = 'link-url',
  LinkedinId = 'linkedin-id',
  LinkedinUrl = 'linkedin-url',
  ListMatches = 'list-matches',
  LivenessCheckScore = 'liveness-check-score',
  MatchFound = 'match-found',
  MatchGroupName = 'match-group-name',
  MatchGroupPassedAttribute = 'match-group-passed-attributes',
  MatchLevels = 'match-levels',
  MatchStatus = 'match-status',
  MatchStatusAddress = 'match-status-address',
  MatchStatusAssociatedPerson = 'match-status-associated-person',
  MatchStatusBusinessName = 'match-status-business-name',
  MatchStatusEin = 'match-status-ein',
  MatchStatusStrength = 'match-status-strength',
  MatchTags = 'match-tags',
  MatchTypes = 'match-types',
  MatchedDataSourceTypes = 'matched-data-source-types',
  MerchantCategoryCodes = 'merchant-category-codes',
  NaicsCodes = 'naics-codes',
  Name = 'name',
  NameBusiness = 'name-business',
  NameComparison = 'name-comparison',
  NameFirst = 'name-first',
  NameLast = 'name-last',
  NameMatch = 'name-match',
  NameMiddle = 'name-middle',
  NativeNameFirst = 'native-name-first',
  NativeNameLast = 'native-name-last',
  NativeNameMiddle = 'native-name-middle',
  NetworkOrganizations = 'network-organizations',
  PassedDatabaseEcbsvVerificationCount = 'passed-database-ecbsv-verification-count',
  PassedDatabaseVerificationCount = 'passed-database-verification-count',
  PassedGovernmentIdVerificationCount = 'passed-government-id-verification-count',
  PassedMatchGroup = 'passed-match-groups',
  PassedSelfieVerificationCount = 'passed-selfie-verification-count',
  PhoneCarrier = 'phone-carrier',
  PhoneCountryCode = 'phone-country-code',
  PhoneNumber = 'phone-number',
  PhoneRiskRecommendations = 'phone-risk-recommendations',
  PhoneType = 'phone-type',
  PoBox = 'po-box',
  PoliticallyExposedPersonClassTypes = 'pep-class-types',
  ProxyDetected = 'proxy-detected',
  QueryAddressTypes = 'query-address-types',
  QueryAddressVacancyStatus = 'query-address-vacancy-status',
  Reasons = 'reasons',
  RecordMatchStatus = 'record-match-status',
  RedirectUri = 'redirect-uri',
  ReferenceId = 'reference-id',
  RegistrationNumberMatchLevel = 'registration-number-match-level',
  RegistrationStatus = 'registration-status',
  RelatedCaseId = 'related-case-id',
  RelatedTxnId = 'related-txn-id',
  RelevanceTypes = 'relevance-types',
  ReportCountries = 'report-countries',
  ReportStatus = 'report-status',
  RequiredChecksFromLatestVerification = 'required-checks-from-latest-verifications',
  ResidentialDeliveryIndicator = 'residential-delivery-indicator',
  ResolvedAddressCity = 'resolved-address-city',
  ResolvedAddressPostalCode = 'resolved-address-postal-code',
  ResolvedAddressStreet1 = 'resolved-address-street1',
  ResolvedAddressStreet2 = 'resolved-address-street2',
  ResolvedAddressSubdivision = 'resolved-address-subdivision',
  RiskLabel = 'risk-label',
  RiskLevel = 'risk-level',
  RiskScore = 'risk-score',
  SelectedCountry = 'selected-country',
  SelfieLivenessRiskLevel = 'selfie-liveness-risk-level',
  SelfiePhoto = 'selfie-photo',
  Selfies = 'selfies',
  ServiceAvailableDetection = 'service-available-detection',
  SessionCount = 'session-count',
  SessionsGeolocationDelta = 'sessions-geolocation-delta',
  SessionsIpLocationDelta = 'sessions-ip-location-delta',
  Sex = 'sex',
  SimilarAccountsBySelfie = 'similar-accounts-by-selfie',
  SocialSecurityNumber = 'social-security-number',
  SsnDigitCount = 'ssn-digit-count',
  Status = 'status',
  SyntheticScore = 'synthetic-score',
  Tags = 'tags',
  ThirdPartySyntheticScore = 'third-party-synthetic-score',
  ThreatLevels = 'threat-levels',
  Tin = 'tin',
  TinMatch = 'tin-match',
  TinType = 'tin-type',
  Token = 'token',
  TorDetected = 'tor-detected',
  TrustedDevices = 'trusted-devices',
  TwitterUrl = 'twitter-url',
  UnsupportedCountry = 'unsupported-country',
  UsState = 'us-state',
  UserRiskScore = 'user-risk-score',
  VantageScore4 = 'vantage-score4',
  VehicleClasses = 'vehicle-classes',
  WatchlistMatch = 'watchlist-match',
  WebsiteMatchLevel = 'website-match-level',
  WorkflowApiKey = 'workflow-api-key',
  ZipType = 'zip-type',
}

export interface ActionMetaBase {
  name: string;
  token: string;
}

export interface ActionMetaBaseWithFieldSchema extends ActionMetaBase {
  fieldSchemas: FieldSchema[];
}

export interface StatusActionMeta extends ActionMetaBase {
  templateToken: string;
  displayName: string;
}

export interface IdentifierActionMeta extends ActionMetaBase {
  templateToken: string;
  displayName: string;
}

export interface ActionMetaBaseWithType extends ActionMetaBase {
  type: string;
}

interface AccountStatusActionMeta extends StatusActionMeta {
  isArchived: boolean;
}

interface AccountTypeActionMeta extends ActionMetaBaseWithFieldSchema {
  relationSchemas: RelationSchema[];
}

interface CaseTemplateActionMeta extends ActionMetaBaseWithFieldSchema {
  status: CaseTemplateStatus;
  prefix: string;
  resolutions: string[]; // TODO(zixiao): cleanup resolutions
  states: CaseState[];
  'assignment-policies': AssignmentPolicy[];
  'field-schema': FieldSchema[];
  'case-message-templates': CaseEmailTemplate[];
}

interface ExternalIntegrationActionMeta extends ActionMetaBaseWithType {
  isArchived: boolean;
}

interface InquiryTemplateActionMeta extends ActionMetaBase {
  latestPublishedVersion: string;
}

interface ListActionMeta extends ActionMetaBaseWithType {
  id: string;
  type: List['type'];
  attributes: ListAttributes;
}

interface ReportTemplateActionMeta extends ActionMetaBaseWithType {
  type: ReportTemplate['type'];
  isArchived: boolean;
}

interface TransactionStatusActionMeta extends StatusActionMeta {
  isActive: boolean;
  transactionType: string;
  templateToken: string;
}

interface TransactionTypeActionMeta extends ActionMetaBaseWithFieldSchema {
  relationSchemas: RelationSchema[];
}

interface VerificationTemplateActionMeta extends ActionMetaBase {
  type: ApiModel;
}

interface VerificationTemplateVersionActionMeta extends ActionMetaBase {
  type: ApiModel;
  verificationTemplateId: string;
}

interface WorkflowTemplateActionMeta extends ActionMetaBase {
  id: string;
  'trigger-type': string;
  status: string;
}

export interface WorkflowActionMeta {
  'account-identifiers': IdentifierActionMeta[];
  'account-identifiers-unique': IdentifierActionMeta[];
  'account-statuses': AccountStatusActionMeta[];
  'account-types': AccountTypeActionMeta[];
  'case-states': StatusActionMeta[];
  'case-templates': CaseTemplateActionMeta[];
  'case-queues': ActionMetaBase[];
  'external-integrations': ExternalIntegrationActionMeta[];
  graph: Graph | null;
  'graph-query-templates': GraphQueryTemplate[];
  'inquiry-templates': InquiryTemplateActionMeta[];
  'inquiry-template-version': {
    token?: string;
    steps?: {
      name: string;
    }[];
  };
  lists: ListActionMeta[];
  'report-templates': ReportTemplateActionMeta[];
  [ApiModel.ReportTemplateEquifaxOneview]: ReportTemplateActionMeta[];
  [ApiModel.ReportTemplateKyckrBusinessLookup]: ReportTemplateActionMeta[];
  [ApiModel.ReportTemplateMiddeskBusinessLookup]: ReportTemplateActionMeta[];
  [ApiModel.ReportTemplateSentilinkApplicationRisk]: ReportTemplateActionMeta[];
  [ApiModel.ReportTemplateSentilinkScores]: ReportTemplateActionMeta[];
  [ApiModel.ReportTemplateChainalysisAddressScreening]: ReportTemplateActionMeta[];
  [ApiModel.ReportTemplateMxAccount]: ReportTemplateActionMeta[];
  [ApiModel.ReportTemplateTrmWalletScreening]: ReportTemplateActionMeta[];
  tags: ActionMetaBase[];
  'transaction-identifiers': IdentifierActionMeta[];
  'transaction-identifiers-unique': IdentifierActionMeta[];
  'transaction-statuses': TransactionStatusActionMeta[];
  'transaction-types': TransactionTypeActionMeta[];
  templates: ActionMetaBase[];
  themes: ActionMetaBase[];
  'theme-sets': ActionMetaBase[];
  users: ActionMetaBase[];
  'verification-templates': VerificationTemplateActionMeta[];
  'verification-template-versions': VerificationTemplateVersionActionMeta[];
  workflows: WorkflowTemplateActionMeta[];
}

export enum WorkflowErrorType {
  Save = 'save',
  Publish = 'publish',
  Run = 'run',
}

interface WorkflowErrorBase {
  type: WorkflowErrorType;
  token: string; // 'trigger' or step's id
  message: string;
}

export interface WorkflowConfigError extends WorkflowErrorBase {
  type: WorkflowErrorType.Save | WorkflowErrorType.Publish;
  field?: string; // specific field name if needed
}

export interface WorkflowRunError extends WorkflowErrorBase {
  type: WorkflowErrorType.Run;
  fields?: string[]; // errored field names if applicable
}

export type WorkflowError = WorkflowConfigError | WorkflowRunError;

export type WorkflowSchema = _WorkflowSchema;
